import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import api from '@/services/apiSgft';
import {
	locationOptions,
	Modality,
	regulatoryModalityOptions,
} from '../../constants/TrainingConstants';
import { Training } from '../../types/Training';
import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import Input from '../atoms/Input';
import Spinner from '../atoms/Spinner';

export interface FormData {
	allPoles: boolean;
	specificLocations: string[];
	name: string;
	modalities: string[];
	totalWorkload: string;
	onlineWorkload: string;
	inPersonWorkload: string;
	validity: string;
	hasNoValidity: boolean;
	needsRecycling: string;
	recyclingMonths: string;
	preRequisites: string[];
	equivalences: string[];
}

interface RegulatoryTrainingFormFieldsProps {
	trainingData?: Training;
	regulatoryTrainings: Training[];
	canSave?: boolean;
	trainingId?: number;
}

const RegulatoryTrainingFormFields: React.FC<
	RegulatoryTrainingFormFieldsProps
> = ({ regulatoryTrainings, canSave, trainingId }) => {
	const { control, reset, register, watch, setValue } = useFormContext();

	// Apenas os watch necessários para lógica condicional
	const selectedModalidade: Array<string> = watch('modalities');
	const needsRecycling = watch('needsRecycling');
	const hasNoValidityWatch = watch('hasNoValidity');
	const regulatoryTrainingOptions =
		regulatoryTrainings?.map((training) => ({
			value: training.id.toString(),
			label: training.name,
		})) || [];

	useEffect(() => {
		if (selectedModalidade?.includes(Modality.Semipresencial)) {
			const onlineWorkload = watch('onlineWorkload');
			const inPersonWorkload = watch('inPersonWorkload');
			const onlineValue = Number(onlineWorkload) || 0;
			const inPersonValue = Number(inPersonWorkload) || 0;
			const total = onlineValue + inPersonValue;
			const roundedTotal = Number.isNaN(total)
				? 0
				: parseFloat(total.toFixed(2));
			setValue('totalWorkload', roundedTotal);
		} else {
			if (selectedModalidade && selectedModalidade.length > 0) {
				setValue('onlineWorkload', 0);
				setValue('inPersonWorkload', 0);
			}
		}
	}, [selectedModalidade, setValue, watch]);

	const fetchTrainingData = async () => {
		try {
			const response = await api.get(`trainings/${trainingId}`);
			return response.data;
		} catch (error) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const enabled= !!trainingId;
	const { data: trainingData, isLoading } = useQuery(
		['trainingData', trainingId],
		fetchTrainingData,
		{
			enabled: !!trainingId,
			retry: false,
		},
	);
	const isTrainingDataLoading = isLoading && enabled;
	useEffect(() => {
		if (trainingData) {
			const hasNoValidity = !trainingData?.validity;
			reset({
				name: trainingData.name ?? '',
				locations: trainingData.locations ?? [],
				modalities: trainingData.modalities ?? [],
				totalWorkload: trainingData.totalWorkload ?? '',
				onlineWorkload: trainingData.onlineWorkload ?? '',
				inPersonWorkload: trainingData.inPersonWorkload ?? '',
				validity: hasNoValidity ? '' : (trainingData.validity ?? 0),
				hasNoValidity: hasNoValidity,
				needsRecycling: trainingData.needsRecycling ?? 'false',
				recyclingMonths: trainingData.recyclingMonths ?? '',
				preRequisites: trainingData.preRequisites ?? [],
				equivalences: trainingData.equivalences ?? [],
				trainingType: trainingData.trainingType ?? '',
			});
		} else {
			reset({
				name: '',
				locations: [],
				modalities: [],
				totalWorkload: '',
				onlineWorkload: '',
				inPersonWorkload: '',
				validity: '',
				hasValidity: 'true',
				recyclingMonths: '',
				preRequisites: [],
				equivalences: [],
				trainingType: '',
			});
		}
	}, [trainingData, reset]);
	const toggleValidity = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { checked: hasNoValidityValue } = e.target;
		if (hasNoValidityValue) {
			setValue('validity', '');
			setValue('hasNoValidity', true);
		} else {
			setValue('validity', '1');
			setValue('hasNoValidity', false);
		}
	};
	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement>
			| React.ChangeEvent<HTMLSelectElement>,
	) => {
		const { type, value } = e.target;
		if (type === 'number' && Number(value) > 999) {
			e.target.value = '999';
		}
	};
	return (
		<>
			{isTrainingDataLoading ? (
				<div className="my-4 flex h-full w-full items-center rounded-md bg-white">
					<Spinner size={40} />
				</div>
			) : (
				<div className="p-5 pt-0">
					<div className="mb-4 w-[46rem]">
						<Input
							label="Nome"
							type="text"
							key={'name'}
							{...register('name', { required: true, onChange: handleChange })}
							required={true}
							disabled={!canSave}
							className="uppercase"
						/>
					</div>
					<div className="mb-4 flex w-[46rem] justify-between gap-4">
						<ControlledSimpleSelect
							values={locationOptions}
							title="Polos de Aplicação"
							control={control}
							required={true}
							key={'locations'}
							name={'locations'}
							multiple={true}
							width="23rem"
							LabelClassName="font-medium text-sm text-primary"
							disabled={!canSave}
						/>
						<ControlledSimpleSelect
							values={regulatoryModalityOptions}
							title="Modalidades"
							control={control}
							required={true}
							multiple={true}
							key={'modalities'}
							name={'modalities'}
							width="22rem"
							LabelClassName="font-medium text-sm text-primary"
							disabled={!canSave}
						/>
					</div>
					<div className="mb-4 flex w-[46rem] justify-between gap-4">
						<Input
							label="Carga Horária Total (h)"
							type="number"
							{...register('totalWorkload', {
								required: true,
								onChange: handleChange,
							})}
							max={999}
							min={0}
							step="0.01"
							required={true}
							disabled={
								!canSave ||
								selectedModalidade?.includes(Modality.Semipresencial)
							}
							key={'totalWorkload'}
							name={'totalWorkload'}
							className="w-[14rem]"
						/>
						<Input
							label="Carga Horária Online (h)"
							type="number"
							{...register('onlineWorkload', {
								required: false,
								onChange: handleChange,
							})}
							max={999}
							min={0}
							step="0.01"
							required={false}
							name={'onlineWorkload'}
							key={'onlineWorkload'}
							className="w-[14rem]"
							disabled={
								!canSave ||
								(selectedModalidade &&
									!selectedModalidade.includes(Modality.Semipresencial))
							}
						/>
						<Input
							label="Carga Horária Presencial (h)"
							step="0.01"
							type="number"
							{...register('inPersonWorkload', {
								required: false,
								onChange: handleChange,
							})}
							max={999}
							min={0}
							required={false}
							name={'inPersonWorkload'}
							key={'inPersonWorkload'}
							disabled={
								!canSave ||
								(selectedModalidade &&
									!selectedModalidade.includes(Modality.Semipresencial))
							}
						/>
					</div>
					<div className="mb-4 flex w-[46rem] justify-between gap-4 items">
						<div className="w-[14rem]">
							<Input
								label="Validade (anos)"
								type="number"
								{...register('validity', {
									required: !hasNoValidityWatch,
									min: !hasNoValidityWatch
										? { value: 1, message: 'Validade deve ser maior que 0' }
										: undefined,

									validate: (value) => {
										if (!hasNoValidityWatch && Number(value) <= 0) {
											return 'Validade deve ser maior que 0';
										}
										return true;
									},
									onChange: handleChange,
								})}
								max={999}
								min={!hasNoValidityWatch ? 1 : 0}
								required={!hasNoValidityWatch}
								name={'validity'}
								key={'validity'}
								disabled={!canSave || hasNoValidityWatch}
							/>
						</div>
						<div className="w-[14rem]">
							<ControlledSimpleSelect
								values={[
									{ value: 'true', label: 'Sim' },
									{ value: 'false', label: 'Não' },
								]}
								title="Necessidade de Reciclagem?"
								control={control}
								required={true}
								key={'needsRecycling'}
								name={'needsRecycling'}
								multiple={false}
								width="14rem"
								LabelClassName="font-medium text-sm text-primary mb-1"
								disabled={!canSave}
							/>
						</div>
						<div className="w-[14rem]">
							<Input
								label="Carga Horária Reciclagem (h)"
								type="number"
								{...register('recyclingMonths', {
									required: needsRecycling === 'true',
									onChange: handleChange,
								})}
								required={needsRecycling === 'true'}
								key={'recyclingMonths'}
								disabled={!canSave || !(needsRecycling === 'true')}
							/>
						</div>
					</div>

					<div className="mb-4 flex w-[46rem] gap-4">
						<ControlledSimpleSelect
							values={regulatoryTrainingOptions}
							title="Pré-Requisitos"
							control={control}
							required={false}
							key={'preRequisites'}
							name={'preRequisites'}
							multiple={true}
							width="22.5rem"
							className="mb-4"
							LabelClassName="font-medium text-sm text-primary"
							disabled={!canSave}
						/>

						<ControlledSimpleSelect
							values={regulatoryTrainingOptions}
							title="Equivalências"
							control={control}
							required={false}
							key={'equivalences'}
							name={'equivalences'}
							multiple={true}
							width="22.5rem"
							LabelClassName="font-medium text-sm text-primary"
							disabled={!canSave}
						/>
					</div>
					<div>
						<div className="w-[15rem] flex items-center">
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									{...register('hasNoValidity', {
										onChange: toggleValidity,
									})}
									disabled={!canSave}
									className="h-4 w-4"
								/>
								<span className="text-sm text-primary font-medium">
									Treinamento não possui validade
								</span>
							</label>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default RegulatoryTrainingFormFields;
